/**
 * BatchProcessCard
 *
 * Displays a single batch process as a compact card.
 * Used in both the list page and the navbar dropdown.
 */

import { Group, Text, Badge, Progress, ActionIcon, Stack } from '@mantine/core';
import { IconRefresh, IconArchive, IconX } from '@tabler/icons-react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { statusConfig, resolveOperationLabel } from '../../../constants';
import { getRenderer, ResolvedData } from '../../../detail/components/operationRenderers';
import styles from './BatchProcessCard.module.css';
import { BatchProcessesReadmodelType } from '@bett3r-dev/teselly-domain';
import { progressPercentage, isFinalizing } from '../../../shared/state/progressSelectors';
import { effectiveStatus as deriveEffectiveStatus } from './effectiveStatus';
import { batchProcessesTestIds } from '../../../testids';
import { CredentialRevokedNotice } from '@domains/integrations/channels/components/CredentialRevokedNotice/CredentialRevokedNotice';

dayjs.extend( relativeTime );

type BatchProcessCardProps = {
  process: BatchProcessesReadmodelType;
  onClick: () => void;
  onRetry?: ( processId: string ) => void;
  onArchive?: ( processId: string ) => void;
  onCancel?: ( processId: string ) => void;
  compact?: boolean;
  t: TFunction;
  resolvedChannels?: ResolvedData['resolvedChannels'];
  resolvedWarehouses?: ResolvedData['resolvedWarehouses'];
  /**
   * Batch Progress count (projection-tied) from `batchProcesses/shared`
   * (TV1-1979). Drives the progress bar via the shared `progressPercentage`
   * selector. `null`/`undefined` falls back to the write-side count until the
   * per-id count roundtrips.
   */
  projectedCount?: number | null;
  /**
   * TV1-2446 slice 6 — the batch's channel target whose credential is revoked, or `null`. While
   * set, Retry is rendered disabled with a Reconnect call to action pointing at it.
   */
  revokedRetryTarget?: string | null;
};

export const BatchProcessCard = ({
  process,
  onClick,
  onRetry,
  onArchive,
  onCancel,
  compact = false,
  t,
  resolvedChannels,
  resolvedWarehouses,
  projectedCount = null,
  revokedRetryTarget = null
}: BatchProcessCardProps ) => {
  const errorCount = process.erroredTargetIds?.length ?? 0;

  // Derive effective status — readmodel sometimes keeps 'running' after completion.
  // SHARED with `BatchProcessMobileRow` (TV1-2294 ticket 48): two copies of this
  // rule is how the desktop and phone cards would end up offering different
  // actions for the same row.
  const effectiveStatus = deriveEffectiveStatus( process );

  const status = statusConfig[effectiveStatus];
  const renderer = getRenderer( process.operationName );
  const summary = renderer.getSummary( process.data, t, process, resolvedChannels, resolvedWarehouses );
  const isRunning = effectiveStatus === 'running';
  const isErrored = effectiveStatus === 'errored';
  // Finalizing = write-side done but user-visible projections still catching up.
  // The card MUST keep showing the bar through this bridge, otherwise it flips to
  // "completed" and hides the bar while items are still trickling in — the exact
  // symptom TV1-1979 fixes for extraction. Mirror the detail page's affordance.
  const finalizing = isFinalizing( process, projectedCount );
  const showProgressBar = isRunning || isErrored || finalizing;
  // Bar tracks Batch Progress (projection-tied projectedCount) via the shared
  // selector — never Batch Completion (completedCount). (TV1-1979 slice 7)
  const progressValue = progressPercentage( process, projectedCount );

  const { i18n } = useTranslation();
  const relativeTimeLabel = dayjs( process.updatedAt ).locale( i18n.language )
.fromNow();

  const handleRetry = ( e: React.MouseEvent ) => {
    e.stopPropagation();
    onRetry?.( process.id );
  };

  const handleArchive = ( e: React.MouseEvent ) => {
    e.stopPropagation();
    onArchive?.( process.id );
  };

  const handleCancel = ( e: React.MouseEvent ) => {
    e.stopPropagation();
    onCancel?.( process.id );
  };

  const cardClassName = compact
    ? `${styles.card} ${styles.compact}`
    : styles.card;

  const actionsClassName = isErrored
    ? `${styles.actions} ${styles.actionsAlwaysVisible}`
    : styles.actions;

  return (
    <div className={cardClassName} onClick={onClick} role="button" tabIndex={0}>
      <Stack gap={compact ? 4 : 8}>
        {/* Top row: status dot + label + time + actions */}
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            {/* Status dot */}
            <div
              className={`${styles.statusDot} ${isRunning ? styles.statusDotRunning : ''}`}
              style={{ backgroundColor: `var(--mantine-color-${status.color}-6)` }}
            />

            {/* Operation label */}
            <Text fw={500} fz={compact ? 12 : 14} truncate="end">
              {resolveOperationLabel( process.operationName, t )}
            </Text>

            {/* Error badge */}
            {errorCount > 0 && (
              <Badge
                size="xs"
                color="red"
                variant="light"
                leftSection={<IconX size={10} />}
              >
                {errorCount}
              </Badge>
            )}
          </Group>

          <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
            {/* Relative time */}
            <Text c="dimmed" fz={compact ? 10 : 12} style={{ whiteSpace: 'nowrap' }}>
              {relativeTimeLabel}
            </Text>

            {/* Action buttons (hidden in compact mode via CSS) */}
            {!compact && (
              <Group gap={2} className={actionsClassName}>
                {onRetry && isErrored && (
                  <ActionIcon
                    variant="transparent"
                    size="sm"
                    radius="xs"
                    color="gray"
                    onClick={handleRetry}
                    disabled={revokedRetryTarget !== null}
                    title={t( 'batchProcesses_actions_retry' )}
                    data-testid={batchProcessesTestIds.listRowAction( 'retry', process.id )}
                  >
                    <IconRefresh size={14} style={{ transition: 'color 0.1s ease' }} className={styles.actionIcon} />
                  </ActionIcon>
                )}
                {onCancel && isRunning && (
                  <ActionIcon
                    variant="transparent"
                    size="sm"
                    radius="xs"
                    color="gray"
                    onClick={handleCancel}
                    title={t( 'batchProcesses_actions_cancel' )}
                  >
                    <IconX size={14} style={{ transition: 'color 0.1s ease' }} className={styles.actionIcon} />
                  </ActionIcon>
                )}
                {onArchive && ( !isRunning ) && (
                  <ActionIcon
                    variant="transparent"
                    size="sm"
                    radius="xs"
                    color="gray"
                    onClick={handleArchive}
                    title={t( 'batchProcesses_actions_archive' )}
                  >
                    <IconArchive size={14} style={{ transition: 'color 0.1s ease' }} className={styles.actionIcon} />
                  </ActionIcon>
                )}
              </Group>
            )}
          </Group>
        </Group>

        {/* Summary text */}
        <Text c="dimmed" fz={compact ? 11 : 12} truncate="end">
          {summary}
        </Text>

        {/* TV1-2446 slice 6 — why Retry is disabled. Its link must not also open the card. */}
        {!compact && onRetry && isErrored && revokedRetryTarget && (
          <div onClick={( e ) => e.stopPropagation()}>
            <CredentialRevokedNotice
              integrationId={revokedRetryTarget}
              message={t( 'batchProcesses_actions_retry_credential_revoked', 'No se puede reintentar: la conexión con el canal fue revocada.' )}
              reconnectLabel={t( 'batchProcesses_actions_reconnect_channel', 'Reconectar canal' )}
              testId={batchProcessesTestIds.listRetryCredentialRevokedNotice( process.id )}
            />
          </div>
        )}

        {/* Progress bar (running or errored only) */}
        {showProgressBar && (
          <Progress
            value={progressValue}
            size={6}
            radius="xl"
            color={status.color}
            animated={isRunning || finalizing}
          />
        )}
      </Stack>
    </div>
  );
};

export default BatchProcessCard;
